import { Link, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FileText } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  SectionLabel,
  Spinner,
  StatCard,
} from '@wundo/ui';
import type {
  SkillRecommendationEntry,
  SkillUserEntry,
  UserSkillOverview,
} from '@wundo/shared/userSkillOverview.ts';
import { fetchUserSkillOverview, userSkillOverviewKey } from '@/lib/users.ts';
import {
  daysSince,
  formatSkillDate,
  recommendationStatusTone,
  relativeFromNow,
  scoreTone,
} from './skillOverviewFormat.ts';
import { UserSkillSchedules } from './UserSkillSchedules.tsx';
import { useState } from 'react';

export interface UserSkillsTabProps {
  userId: string;
}

/**
 * What the skill engine has done for this person, and what it is about to do:
 * the skill their conversation is in now, the ones they used before, what is
 * scheduled to fire, and what the weekly run last proposed.
 */
export function UserSkillsTab({ userId }: UserSkillsTabProps) {
  const {
    data: overview,
    isPending,
    isError,
  } = useQuery({
    queryKey: userSkillOverviewKey(userId),
    queryFn: () => fetchUserSkillOverview(userId),
  });

  if (isPending) {
    return (
      <Card className='flex justify-center'>
        <Spinner />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <EmptyState
          title='Failed to load skill overview'
          description='Try again in a moment.'
        />
      </Card>
    );
  }

  const activeSchedules = overview.schedules.filter((schedule) => schedule.active);
  const pending = overview.recommendations.filter(
    (entry) => entry.status === 'pending',
  ).length;

  return (
    <div className='flex flex-col gap-5'>
      <div className='grid grid-cols-2 gap-3 agenda:grid-cols-4'>
        <StatCard label='Active' value={overview.active_skill?.skill_name ?? '—'} />
        <StatCard
          label='Used'
          value={overview.skill_users.length}
          hint={overview.skill_users.length === 1 ? 'skill' : 'skills'}
        />
        <StatCard label='Schedules' value={activeSchedules.length} hint='active' />
        <StatCard label='Pending rec' value={pending} />
      </div>

      <ActiveSkillCard overview={overview} />
      <UserSkillUsage skillUsers={overview.skill_users} />
      <UserSkillSchedules
        userId={userId}
        schedules={overview.schedules}
        timezone={overview.user.timezone}
      />
      <UserSkillRecommendations
        recommendations={overview.recommendations}
        userId={userId}
      />
    </div>
  );
}

function ActiveSkillCard({ overview }: { overview: UserSkillOverview }) {
  const active = overview.active_skill;

  if (!active) {
    return (
      <Card>
        <EmptyState title="No skill currently active in this user's conversation." />
      </Card>
    );
  }

  return (
    <Card className='flex items-start justify-between gap-4'>
      <div className='min-w-0'>
        <SectionLabel>Active skill</SectionLabel>
        <Link
          to='/skills/$id/edit'
          params={{ id: active.skill_id }}
          className='mt-1 block text-lg font-semibold text-ink'
        >
          {active.skill_name}
        </Link>
        <p className='mt-1 flex flex-wrap items-center gap-3 text-xs text-ink-muted'>
          <span>{`Switched ${relativeFromNow(active.switched_at)}`}</span>
          <span className='font-mono'>{active.conversation_id.slice(0, 8)}</span>
        </p>
      </div>
      <Link
        to='/skills/$id/edit'
        params={{ id: active.skill_id }}
        aria-label={`Open ${active.skill_name}`}
        className='shrink-0 text-ink hover:underline'
      >
        <ArrowRight aria-hidden='true' className='size-5' />
      </Link>
    </Card>
  );
}

function UserSkillUsage({ skillUsers }: { skillUsers: SkillUserEntry[] }) {
  const [notesFor, setNotesFor] = useState<SkillUserEntry | null>(null);

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex items-end justify-between gap-4'>
        <div>
          <SectionLabel as='h2'>Skill usage</SectionLabel>
          <p className='mt-1 text-xs text-ink-muted'>
            Freshness scoring uses <code className='font-mono'>last_used_at</code> — newer
            use = lower recommendation score.
          </p>
        </div>
        <p className='text-xs whitespace-nowrap text-ink-muted'>
          {`${skillUsers.length} ${skillUsers.length === 1 ? 'skill' : 'skills'}`}
        </p>
      </div>

      {skillUsers.length === 0 ? (
        <Card>
          <EmptyState title='No skills used yet.' />
        </Card>
      ) : (
        <Card padding='none' className='overflow-hidden'>
          <ul>
            {skillUsers.map((entry) => (
              <li
                key={entry.skill_id}
                className='flex items-center gap-4 border-t border-fill px-5.5 py-3 first:border-t-0'
              >
                <div className='min-w-0 flex-1'>
                  <Link
                    to='/skills/$id/edit'
                    params={{ id: entry.skill_id }}
                    className='text-sm font-semibold text-ink hover:underline'
                  >
                    {entry.skill_name}
                  </Link>
                  <p className='mt-0.5 text-xs text-ink-muted'>
                    {entry.last_used_at
                      ? `${daysSince(entry.last_used_at)}d ago · ${formatSkillDate(
                          entry.last_used_at,
                        )}`
                      : 'never used'}
                  </p>
                </div>
                {entry.notes ? (
                  <Button variant='ghost' size='sm' onClick={() => setNotesFor(entry)}>
                    <FileText aria-hidden='true' className='size-4' />
                    View notes
                  </Button>
                ) : (
                  <span className='text-xs text-ink-muted'>no notes</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Dialog
        open={notesFor !== null}
        onOpenChange={(open) => !open && setNotesFor(null)}
      >
        <DialogContent size='md'>
          <DialogHeader>
            <DialogTitle>
              {notesFor ? `Notes — ${notesFor.skill_name}` : 'Notes'}
            </DialogTitle>
          </DialogHeader>
          <div className='px-5 py-4'>
            <p className='max-h-[60vh] overflow-y-auto rounded-md border border-line bg-fill-faint p-4 text-sm break-words whitespace-pre-wrap text-ink'>
              {notesFor?.notes}
            </p>
          </div>
          <DialogFooter>
            <Button variant='quiet' onClick={() => setNotesFor(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function UserSkillRecommendations({
  recommendations,
  userId,
}: {
  recommendations: SkillRecommendationEntry[];
  userId: string;
}) {
  const router = useRouter();

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex items-end justify-between gap-4'>
        <div>
          <SectionLabel as='h2'>Recent recommendations</SectionLabel>
          <p className='mt-1 text-xs text-ink-muted'>
            Weekly outreaches from the recommendation engine. Click for full scoring log.
          </p>
        </div>
        <p className='text-xs whitespace-nowrap text-ink-muted'>
          {`${recommendations.length} total`}
        </p>
      </div>

      {recommendations.length === 0 ? (
        <Card>
          <EmptyState title='No recommendations yet.' />
        </Card>
      ) : (
        <ol className='flex flex-col gap-3'>
          {recommendations.map((entry) => (
            <li key={entry.id}>
              <Button
                variant='quiet'
                fullWidth
                shape='block'
                className='flex-col items-stretch gap-1 text-left'
                onClick={() =>
                  void router.navigate({
                    to: '/admin/recommendation-runs/$runId/users/$userId',
                    params: { runId: entry.run_id, userId },
                  })
                }
              >
                <span className='flex items-center justify-between gap-3'>
                  <span className='flex min-w-0 items-center gap-2'>
                    <span className='truncate text-sm font-semibold text-ink'>
                      {entry.skill_name}
                    </span>
                    <Badge tone={scoreTone(entry.final_score)} className='font-mono'>
                      {entry.final_score.toFixed(1)}
                    </Badge>
                    <Badge tone={recommendationStatusTone(entry.status)}>
                      {entry.status}
                    </Badge>
                  </span>
                  <span className='text-xs whitespace-nowrap text-ink-muted'>
                    {formatSkillDate(entry.recommended_at)}
                  </span>
                </span>
                <span className='text-sm font-normal text-ink-secondary'>
                  {entry.skipped_reason ?? entry.reason}
                </span>
              </Button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
